import type { AudioAssetName } from "./audio-assets";
import { IMAGES } from "./images";

export type AudioFile = {
  baseFileName: AudioAssetName;
  durationMs: number;
};

const audioFile = (baseFileName: AudioAssetName, durationMs: number) =>
  ({ baseFileName, durationMs }) as const satisfies AudioFile;

export type SyllableTiming = {
  syllable: string;
  startSeconds: number;
  durationSeconds: number;
};

type ProdWord = {
  audio: AudioFile;
  word: string;
  syllableCount: number;
  stressIndex: number;
  syllables: readonly string[];
  displaySyllables: readonly string[];
  syllableTimings: readonly SyllableTiming[];
  image: (typeof IMAGES)[keyof typeof IMAGES];
};

export const PROD = [
  {
    audio: audioFile("S2_PRD_2_2_casa", 554),
    word: "casa",
    syllableCount: 2,
    stressIndex: 0,
    syllables: ["k a", "s a"],
    displaySyllables: ["CA", "sa"],
    syllableTimings: [
      { syllable: "ca", startSeconds: 0.0688, durationSeconds: 0.1715 },
      { syllable: "sa", startSeconds: 0.2403, durationSeconds: 0.2616 },
    ],
    image: IMAGES.casa,
  },
  {
    audio: audioFile("S2_PRD_3_2_cocina", 589),
    word: "cocina",
    syllableCount: 3,
    stressIndex: 1,
    syllables: ["k o", "T i", "n a"],
    displaySyllables: ["co", "CI", "na"],
    syllableTimings: [
      { syllable: "co", startSeconds: 0.0169, durationSeconds: 0.1316 },
      { syllable: "ci", startSeconds: 0.1485, durationSeconds: 0.2014 },
      { syllable: "na", startSeconds: 0.3499, durationSeconds: 0.1656 },
    ],
    image: IMAGES.cocina,
  },
  {
    audio: audioFile("S3_PRD_3_1_catedral", 830),
    word: "catedral",
    syllableCount: 3,
    stressIndex: 2,
    syllables: ["k a", "t e", "D r a l"],
    displaySyllables: ["ca", "te", "DRAL"],
    syllableTimings: [
      { syllable: "ca", startSeconds: 0.0412, durationSeconds: 0.1279 },
      { syllable: "te", startSeconds: 0.1691, durationSeconds: 0.1758 },
      { syllable: "dral", startSeconds: 0.3449, durationSeconds: 0.3794 },
    ],
    image: IMAGES.catedral,
  },
  {
    audio: audioFile("S3_PRD_3_3_platano", 699),
    word: "platano",
    syllableCount: 3,
    stressIndex: 0,
    syllables: ["p l a", "t a", "n o"],
    displaySyllables: ["PLÁ", "ta", "no"],
    syllableTimings: [
      { syllable: "pla", startSeconds: 0.0472, durationSeconds: 0.228 },
      { syllable: "ta", startSeconds: 0.2752, durationSeconds: 0.1759 },
      { syllable: "no", startSeconds: 0.4511, durationSeconds: 0.1913 },
    ],
    image: IMAGES.platano,
  },
  {
    audio: audioFile("S3_PRD_4_2_bicicleta", 947),
    word: "bicicleta",
    syllableCount: 4,
    stressIndex: 2,
    syllables: ["B i", "T i", "k l e", "t a"],
    displaySyllables: ["bi", "ci", "CLE", "ta"],
    syllableTimings: [
      { syllable: "bi", startSeconds: 0.0336, durationSeconds: 0.1361 },
      { syllable: "ci", startSeconds: 0.1697, durationSeconds: 0.1574 },
      { syllable: "cle", startSeconds: 0.3272, durationSeconds: 0.2596 },
      { syllable: "ta", startSeconds: 0.5868, durationSeconds: 0.2568 },
    ],
    image: IMAGES.bicicleta,
  },
] as const satisfies ProdWord[];

export type TrainWord = {
  audio: AudioFile;
  word: string;
  syllableCount: number;
  stressIndex: number;
};

export const TRAIN = [
  {
    audio: audioFile("S4_TRN_3_2_cubico", 788),
    word: "cubico",
    syllableCount: 3,
    stressIndex: 1,
  },
  {
    audio: audioFile("S3_TRN_3_1_publico", 737),
    word: "publico",
    syllableCount: 3,
    stressIndex: 2,
  },
  {
    audio: audioFile("S6_TRN_3_2_titulo", 625),
    word: "titulo",
    syllableCount: 3,
    stressIndex: 1,
  },
  {
    audio: audioFile("S3_TRN_3_3_cantara", 808),
    word: "cantara",
    syllableCount: 3,
    stressIndex: 0,
  },
  {
    audio: audioFile("S6_TRN_3_2_calculo", 703),
    word: "calculo",
    syllableCount: 3,
    stressIndex: 1,
  },
  {
    audio: audioFile("S4_TRN_3_3_jubilo", 822),
    word: "jubilo",
    syllableCount: 3,
    stressIndex: 0,
  },
  {
    audio: audioFile("S6_TRN_3_1_circulo", 765),
    word: "circulo",
    syllableCount: 3,
    stressIndex: 2,
  },
  {
    audio: audioFile("S2_TRN_3_3_publico", 656),
    word: "publico",
    syllableCount: 3,
    stressIndex: 0,
  },
  {
    audio: audioFile("S3_TRN_3_2_jubilo", 796),
    word: "jubilo",
    syllableCount: 3,
    stressIndex: 1,
  },
  {
    audio: audioFile("S4_TRN_3_1_liquido", 669),
    word: "liquido",
    syllableCount: 3,
    stressIndex: 2,
  },
] as const satisfies TrainWord[];

export type OmoWord = {
  matchingAudios: readonly AudioFile[];
  matchingStressIndex: number;
  oddOneAudio: AudioFile;
  oddOneStressIndex: number;
  oddOnePosition: number;
  word: string;
  syllableCount: number;
};

export const OMO = [
  {
    matchingAudios: [
      audioFile("S1_OMO_3_3_vinculo_r1", 769),
      audioFile("S1_OMO_3_3_vinculo_r2", 727),
    ],
    matchingStressIndex: 0,
    oddOneAudio: audioFile("S1_OMO_3_2_vinculo_r1", 925),
    oddOneStressIndex: 1,
    oddOnePosition: 0,
    word: "vinculo",
    syllableCount: 3,
  },
  {
    matchingAudios: [
      audioFile("S1_OMO_3_2_numero_r1", 743),
      audioFile("S1_OMO_3_2_numero_r2", 805),
    ],
    matchingStressIndex: 1,
    oddOneAudio: audioFile("S1_OMO_3_3_numero_r1", 746),
    oddOneStressIndex: 0,
    oddOnePosition: 2,
    word: "numero",
    syllableCount: 3,
  },
  {
    matchingAudios: [
      audioFile("S1_OMO_3_2_mascara_r1", 745),
      audioFile("S1_OMO_3_2_mascara_r2", 777),
    ],
    matchingStressIndex: 1,
    oddOneAudio: audioFile("S1_OMO_3_1_mascara_r1", 780),
    oddOneStressIndex: 2,
    oddOnePosition: 1,
    word: "mascara",
    syllableCount: 3,
  },
  {
    matchingAudios: [
      audioFile("S1_OMO_3_2_limite_r1", 814),
      audioFile("S1_OMO_3_2_limite_r2", 896),
    ],
    matchingStressIndex: 1,
    oddOneAudio: audioFile("S1_OMO_3_1_limite_r1", 740),
    oddOneStressIndex: 2,
    oddOnePosition: 0,
    word: "limite",
    syllableCount: 3,
  },
  {
    matchingAudios: [
      audioFile("S1_OMO_3_1_mascara_r1", 780),
      audioFile("S7_OMO_3_1_mascara_r1", 708),
    ],
    matchingStressIndex: 2,
    oddOneAudio: audioFile("S1_OMO_3_2_mascara_r1", 745),
    oddOneStressIndex: 1,
    oddOnePosition: 2,
    word: "mascara",
    syllableCount: 3,
  },
  {
    matchingAudios: [
      audioFile("S1_OMO_3_2_valido_r1", 803),
      audioFile("S7_OMO_3_2_valido_r1", 694),
    ],
    matchingStressIndex: 1,
    oddOneAudio: audioFile("S1_OMO_3_3_valido_r1", 780),
    oddOneStressIndex: 0,
    oddOnePosition: 1,
    word: "valido",
    syllableCount: 3,
  },
  {
    matchingAudios: [
      audioFile("S1_OMO_3_2_palpito_r1", 821),
      audioFile("S7_OMO_3_2_palpito_r1", 769),
    ],
    matchingStressIndex: 1,
    oddOneAudio: audioFile("S1_OMO_3_1_palpito_r1", 791),
    oddOneStressIndex: 2,
    oddOnePosition: 0,
    word: "palpito",
    syllableCount: 3,
  },
  {
    matchingAudios: [
      audioFile("S1_OMO_3_1_picara_r1", 651),
      audioFile("S7_OMO_3_1_picara_r1", 557),
    ],
    matchingStressIndex: 2,
    oddOneAudio: audioFile("S1_OMO_3_3_picara_r1", 654),
    oddOneStressIndex: 0,
    oddOnePosition: 2,
    word: "picara",
    syllableCount: 3,
  },
  {
    matchingAudios: [
      audioFile("S1_OMO_3_1_domine_r1", 703),
      audioFile("S7_OMO_3_1_domine_r1", 643),
    ],
    matchingStressIndex: 2,
    oddOneAudio: audioFile("S1_OMO_3_2_domine_r1", 545),
    oddOneStressIndex: 1,
    oddOnePosition: 1,
    word: "domine",
    syllableCount: 3,
  },
  {
    matchingAudios: [
      audioFile("S7_OMO_3_2_termino_r1", 625),
      audioFile("S1_OMO_3_2_termino_r1", 729),
    ],
    matchingStressIndex: 1,
    oddOneAudio: audioFile("S1_OMO_3_3_termino_r1", 691),
    oddOneStressIndex: 0,
    oddOnePosition: 2,
    word: "termino",
    syllableCount: 3,
  },
] as const satisfies OmoWord[];
